/**
 * FirewallRulesTable Component
 *
 * Displays firewall rules in a table with sorting, filtering, drag-and-drop reordering, and actions.
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ShieldIcon,
  Edit3Icon,
  Trash2Icon,
  GripVerticalIcon,
  ArrowDownToLineIcon,
  ArrowUpFromLineIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
  AlertTriangleIcon,
  Loader2Icon,
} from 'lucide-react'
import { useState, useMemo } from 'react'

import {
  getDirectionLabel,
  getDirectionColor,
  getActionLabel,
  getActionColor,
  getProtocolLabel,
  getEnabledColor,
  getEnabledLabel,
  formatPort,
  formatSource,
} from '@nebula/shared/services'
import type { FirewallRule } from '@nebula/shared/types/firewall'
import { getApiErrorText } from '@nebula/shared/utils'

interface FirewallRulesTableProps {
  rules: FirewallRule[]
  isLoading?: boolean
  onEdit: (rule: FirewallRule) => void
  onDelete: (rule: FirewallRule) => void
  onToggle: (rule: FirewallRule) => void | Promise<void>
  onReorder?: (ruleIds: string[]) => Promise<void>
  emptyMessage?: string
}

// Sortable Row Component
interface SortableRowProps {
  rule: FirewallRule
  index: number
  isDraggable: boolean
  deleteConfirm: string | null
  onDeleteClick: (rule: FirewallRule) => void
  onEdit: (rule: FirewallRule) => void
  onToggle: (rule: FirewallRule) => void | Promise<void>
}

const SortableRow = ({
  rule,
  index,
  isDraggable,
  deleteConfirm,
  onDeleteClick,
  onEdit,
  onToggle,
}: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 ${!rule.enabled ? 'opacity-60' : ''} ${
        isDragging ? 'bg-purple-50 shadow-lg' : ''
      }`}
    >
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-1">
          {isDraggable && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab rounded p-1 hover:bg-gray-100 active:cursor-grabbing"
              title="Arrastar para reordenar"
            >
              <GripVerticalIcon className="h-4 w-4 text-gray-400" />
            </button>
          )}
          <span className="text-sm text-gray-500">{index + 1}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="max-w-xs">
          <div className="truncate text-sm font-medium text-gray-900">
            {rule.name || `Regra ${index + 1}`}
          </div>
          {rule.comment && <div className="truncate text-xs text-gray-500">{rule.comment}</div>}
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getDirectionColor(
            rule.direction,
          )}`}
        >
          {rule.direction === 'in' ? (
            <ArrowDownToLineIcon className="mr-1 h-3 w-3" />
          ) : (
            <ArrowUpFromLineIcon className="mr-1 h-3 w-3" />
          )}
          {getDirectionLabel(rule.direction)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getActionColor(
            rule.action,
          )}`}
        >
          {getActionLabel(rule.action)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-mono text-sm text-gray-900">{getProtocolLabel(rule.protocol)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-mono text-sm text-gray-900">{formatPort(rule.dport)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-mono text-sm text-gray-900">{formatSource(rule.source)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-mono text-sm text-gray-900">{rule.iface || 'Todas'}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getEnabledColor(
            rule.enabled,
          )}`}
        >
          {getEnabledLabel(rule.enabled)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onToggle(rule)}
            className="rounded p-1 text-gray-400 hover:bg-purple-50 hover:text-purple-600"
            title={rule.enabled ? 'Desativar' : 'Ativar'}
          >
            {rule.enabled ? (
              <ToggleRightIcon className="h-4 w-4 text-green-500" />
            ) : (
              <ToggleLeftIcon className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => onEdit(rule)}
            className="rounded p-1 text-gray-400 hover:bg-purple-50 hover:text-purple-600"
            title="Editar"
          >
            <Edit3Icon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDeleteClick(rule)}
            className={`rounded p-1 ${
              deleteConfirm === rule.id
                ? 'bg-red-50 text-red-600'
                : 'text-gray-400 hover:bg-red-50 hover:text-red-600'
            }`}
            title={deleteConfirm === rule.id ? 'Clique novamente para confirmar' : 'Excluir'}
          >
            {deleteConfirm === rule.id ? (
              <AlertTriangleIcon className="h-4 w-4" />
            ) : (
              <Trash2Icon className="h-4 w-4" />
            )}
          </button>
        </div>
      </td>
    </tr>
  )
}

const FirewallRulesTable = ({
  rules,
  isLoading = false,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
  emptyMessage = 'Nenhuma regra de firewall configurada',
}: FirewallRulesTableProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [isReordering, setIsReordering] = useState(false)
  // Recusa do backend numa ação da tabela (excluir, reordenar). O motivo é escrito para
  // o cliente — não pode morrer numa promise rejeitada nem no console.
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null)

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum distance before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Get rule IDs for sortable context
  const ruleIds = useMemo(() => rules.map((rule) => rule.id), [rules])

  const handleDeleteClick = async (rule: FirewallRule) => {
    if (deleteConfirm === rule.id) {
      setDeleteConfirm(null)
      setErroDaAcao(null)
      try {
        // Com `await` e `catch`: o backend passou a RECUSAR a exclusão que não pode
        // provar no Proxmox (409, e 400/503 quando ele recusa ou não responde), com
        // mensagens escritas para o cliente. Sem isto, a promise rejeitada sumia, a
        // regra continuava na lista e nada aparecia — do lado de cá o botão
        // simplesmente não funcionava.
        await onDelete(rule)
      } catch (error) {
        // O degrau do meio (`error.message`) não é enfeite: quem chama levanta um
        // `Error` próprio quando a resposta chega 2xx com `sync_error`, e esse objeto
        // não tem `response` — sem ele, a faixa trocava o motivo escrito para o cliente
        // por um genérico que diz o OPOSTO do que houve ("não foi possível alterar"
        // quando a alteração foi gravada e só não chegou ao firewall).
        setErroDaAcao(
          getApiErrorText(error) || (error as Error)?.message || 'Não foi possível remover a regra',
        )
      }
    } else {
      setDeleteConfirm(rule.id)
      // Auto-reset after 3 seconds
      setTimeout(() => setDeleteConfirm(null), 3000)
    }
  }

  const handleToggleClick = async (rule: FirewallRule) => {
    setErroDaAcao(null)
    try {
      // Habilitar/desabilitar é uma edição, e o backend pode recusá-la (409 de
      // identidade ambígua, 400/503 do Proxmox). Sem `await`/`catch` a recusa virava
      // promise não tratada e o toggle voltava sozinho, sem explicação.
      await onToggle(rule)
    } catch (error) {
      setErroDaAcao(
        getApiErrorText(error) || (error as Error)?.message || 'Não foi possível alterar a regra',
      )
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && onReorder) {
      const oldIndex = rules.findIndex((rule) => rule.id === active.id)
      const newIndex = rules.findIndex((rule) => rule.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        // Calculate new order
        const reorderedRules = arrayMove(rules, oldIndex, newIndex)
        const newRuleIds = reorderedRules.map((rule) => rule.id)

        // Call API to persist new order
        setIsReordering(true)
        try {
          await onReorder(newRuleIds)
        } catch (error) {
          // A ordem decide qual regra casa primeiro: falhar calado deixa o cliente
          // vendo a ordem voltar sozinha, sem saber por quê.
          setErroDaAcao(
            getApiErrorText(error) ||
              (error as Error)?.message ||
              'Não foi possível aplicar a nova ordem das regras de firewall',
          )
        } finally {
          setIsReordering(false)
        }
      }
    }
  }

  if (isLoading) {
    return (
      <div
        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
        data-testid="firewall-rules-loading"
      >
        <div className="animate-pulse space-y-4 p-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex space-x-4">
              <div className="h-4 w-1/4 rounded bg-gray-200"></div>
              <div className="h-4 w-1/6 rounded bg-gray-200"></div>
              <div className="h-4 w-1/6 rounded bg-gray-200"></div>
              <div className="h-4 w-1/4 rounded bg-gray-200"></div>
              <div className="h-4 w-1/6 rounded bg-gray-200"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (rules.length === 0) {
    return (
      <div
        className="rounded-lg border border-gray-200 bg-white p-8 text-center"
        data-testid="firewall-no-rules"
      >
        <ShieldIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
        <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma Regra</h3>
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    )
  }

  const isDraggable = !!onReorder && rules.length > 1

  return (
    <div
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-testid="firewall-rules-table"
    >
      {/* Reordering indicator */}
      {isReordering && (
        <div className="flex items-center space-x-2 border-b border-purple-200 bg-purple-50 px-4 py-2">
          <Loader2Icon className="h-4 w-4 animate-spin text-purple-600" />
          <span className="text-sm text-purple-700">Salvando nova ordem...</span>
        </div>
      )}

      {/* Drag hint */}
      {isDraggable && !isReordering && (
        <div className="flex items-center space-x-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
          <GripVerticalIcon className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-500">
            Arraste as linhas pelo ícone <GripVerticalIcon className="inline h-3 w-3" /> para
            reordenar as regras
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className="min-w-full divide-y divide-gray-200" role="table">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-16 px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Direção
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Ação
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Protocolo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Porta
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Origem
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Interface
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              <SortableContext items={ruleIds} strategy={verticalListSortingStrategy}>
                {rules.map((rule, index) => (
                  <SortableRow
                    key={rule.id}
                    rule={rule}
                    index={index}
                    isDraggable={isDraggable}
                    deleteConfirm={deleteConfirm}
                    onDeleteClick={handleDeleteClick}
                    onEdit={onEdit}
                    onToggle={handleToggleClick}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>

      {/* Recusa de uma ação da tabela — fica junto da lista onde a ação foi disparada. */}
      {erroDaAcao && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{erroDaAcao}</span>
            <button
              onClick={() => setErroDaAcao(null)}
              className="text-red-500 hover:text-red-700"
              aria-label="Dispensar erro"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Sync status footer */}
      {rules.some((r) => r.sync_error) && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-center space-x-2 text-sm text-red-700">
            <AlertTriangleIcon className="h-4 w-4" />
            <span>Algumas regras de firewall não foram aplicadas corretamente</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default FirewallRulesTable
